// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.

import { formatToMoney } from '@advisor-app/features/utils/format-to-money';
import { Dialog, DialogContent, IconButton, Skeleton } from '@mui/material';
import { AppIcon, Heading } from '@shared/ui-kit/components';
import { type FC, useCallback, useMemo } from 'react';
import { type OrderStatus } from '../hooks/use-execute-trades';
import { useTranslation } from 'react-i18next';
import {
  OperationType,
  normalizeOperationType,
} from '@shared/data-access/interfaces/operation-type';

export interface OrdersSummaryProps {
  open: boolean;
  handleClose: () => void;
  orders: OrderStatus[];
  loading: boolean;
}

interface OrderStatusColor {
  label: string;
  color: string;
}

const statusColor: { [key: string]: OrderStatusColor } = {
  buy: { label: '', color: 'text-primary-green' },
  error: { label: '', color: 'text-primary-red' },
  sell: { label: '', color: 'text-primary-pink' },
};

export const OrdersStatus: FC<OrdersSummaryProps> = ({
  handleClose,
  open,
  orders,
  loading,
}) => {
  const { t } = useTranslation();

  const getStatus = useCallback(
    (order: OrderStatus) => {
      const isFailed = order.status === 'error';
      let operation: string;
      if (isFailed) {
        operation = 'error';
      } else {
        try {
          const normalized = normalizeOperationType(order.operation);
          operation = normalized.toLowerCase();
        } catch {
          operation = order.operation.toLowerCase();
        }
      }
      const baseStatus = statusColor[operation];

      let label = '';
      if (operation === 'buy') {
        label = t('brokers.bought', { ns: 'components' });
      } else if (operation === 'sell') {
        label = t('brokers.sold', { ns: 'components' });
      } else if (operation === 'error') {
        label = t('brokers.failed', { ns: 'components' });
      }

      return {
        ...baseStatus,
        label,
      };
    },
    [t],
  );

  const summary = useMemo(() => {
    const buy = orders.filter(order => {
      try {
        return normalizeOperationType(order.operation) === OperationType.BUY;
      } catch {
        return order.operation.toLowerCase() === 'buy';
      }
    });
    const sell = orders.filter(order => {
      try {
        return normalizeOperationType(order.operation) === OperationType.SELL;
      } catch {
        return order.operation.toLowerCase() === 'sell';
      }
    });
    return {
      buyOrders: buy.length,
      sellOrders: sell.length,
      successBuyOrders: buy.filter(order => order.status !== 'error').length,
      successSellOrders: sell.filter(order => order.status !== 'error').length,
    };
  }, [orders]);

  return (
    <Dialog
      open={open}
      maxWidth="md"
      scroll="paper"
      disableScrollLock
      classes={{ paper: '!rounded-lg' }}
    >
      <IconButton
        aria-label="close"
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
        }}
      >
        <AppIcon icon="Close" />
      </IconButton>
      <DialogContent className="max-h-[900px] w-[400px] rounded !p-[27px]">
        <div className="">
          <div className="grid justify-center gap-6">
            <Heading
              variant="h2"
              title={t('brokers.trades-processing', { ns: 'components' })}
            />
            <div className="flex justify-between text-border-dark">
              <div className="flex">
                <span className="mr-2 font-bold">
                  {t('brokers.sell', { ns: 'components' })}:{' '}
                </span>
                {loading ? (
                  <Skeleton variant="text" sx={{ fontSize: '1rem' }} width={40} />
                ) : (
                  <span>
                    {summary.successSellOrders}/{summary.sellOrders}
                  </span>
                )}
              </div>
              <div className="flex">
                <span className="mr-2 font-bold">
                  {t('brokers.buy', { ns: 'components' })}:{' '}
                </span>
                {loading ? (
                  <Skeleton variant="text" sx={{ fontSize: '1rem' }} width={40} />
                ) : (
                  <span>
                    {summary.successBuyOrders}/{summary.buyOrders}
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="text-primary-medium" data-testid="orders-list">
            {orders.map(order => (
              <div
                key={order.ticker + order.operation}
                className="flex justify-between border-b border-border-light py-3"
              >
                <div className="flex">
                  <img
                    className="aspect-square mt-1 h-[25px] w-[25px] rounded-full"
                    src={order.logo}
                    alt={order.ticker}
                  />
                  <div className="ml-2 flex flex-col">
                    <span className="font-medium leading-6">{order.ticker}</span>
                    <span className="text-sm">
                      {t('brokers.quantity', { ns: 'components' })}: {order.qty}
                    </span>
                  </div>
                </div>
                <div className="flex flex-col">
                  <span>{formatToMoney(order.volume)}</span>
                  {loading ? (
                    <Skeleton variant="text" sx={{ fontSize: '1rem' }} />
                  ) : (
                    <span className={`self-end text-sm ${getStatus(order).color}`}>
                      {getStatus(order).label}
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
